import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Output,
  Input,
  EventEmitter,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DocumentCategoryViewModel } from 'src/app/modules/shared/viewModels/documents/documentCategoryViewModel';
import { Configuration } from 'src/app/app.constants';
import { FileValidationService, FileValidationResult } from '../../../../services/file-validation.service';
import { CapacitorUtilities } from 'src/app/modules/shared/utilities/capacitor.utilities';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { v4 as uuidv4 } from 'uuid';
import { T } from 'src/assets/i18n/translation-keys';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-upload-container',
    templateUrl: 'upload-container.component.html',
    styleUrls: ['upload-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UploadContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('file') fileElement: ElementRef<HTMLElement>;
  private subscriptions: Subscription[] = [];
  private initTimeout: ReturnType<typeof setTimeout> | null = null;

  @Input() globalObjectId: number;
  @Input() canAddLink: boolean = true;
  @Input() canAddFile: boolean = true;
  @Input() disableDragAndDrop: boolean = false;
  @Input() externalLoading: boolean = false; // Allow parent to control loading state

  @Input() showLinkOption: boolean = true;
  @Input() showHelperText: boolean = true;
  @Input() showLabel: boolean = true;
  @Input() customButtonText: string = '';
  @Input() allowedTypes?: string[];
  @Input() maxFiles?: number;
  @Input() showFileTypeInfo: boolean = false;

  @Output() onFilesAdded = new EventEmitter<File[]>();
  @Output() onLinkAdded = new EventEmitter<string>();

  public isDragOver: boolean = false;
  private dragCounter: number = 0;
  public shouldDisableDragAndDrop: boolean = false;

  url: any;
  loading: boolean = false;
  public documentCategories: DocumentCategoryViewModel[] = [];
  wrapperIsSmall: boolean = false;
  wrapperIsLarge: boolean = false;
  public isNativePlatform: boolean = false;
  public readonly T = T;

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData && clipboardData.files && clipboardData.files.length > 0) {
      this.loading = true;
      this.onSelectFile(clipboardData.files);
    }
  }

  @HostListener('window:paste', ['$event'])
  onGlobalPaste(event: ClipboardEvent) {
    if (!this.canAddFile) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
      return;
    }

    const clipboardData = event.clipboardData;
    if (clipboardData && clipboardData.files && clipboardData.files.length > 0) {
      event.preventDefault();
      this.loading = true;
      this.onSelectFile(clipboardData.files);
    }
  }

  constructor(
    private readonly configuration: Configuration,
    private readonly fileValidationService: FileValidationService,
    private readonly alertService: AlertService,
    private readonly translateService: TranslateService,
    private readonly elementRef: ElementRef,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isNativePlatform = CapacitorUtilities.isNative;
    this.checkForScopedDragDrop();
  }

  ngAfterViewInit(): void {
    this.initTimeout = setTimeout(() => {
      this.checkForScopedDragDrop();
      this.initTimeout = null;
    }, 0);
  }

  private checkForScopedDragDrop(): void {
    if (this.isNativePlatform) {
      this.shouldDisableDragAndDrop = true;
      return;
    }

    if (this.disableDragAndDrop) {
      this.shouldDisableDragAndDrop = true;
      return;
    }

    let currentElement = this.elementRef.nativeElement.parentElement;
    while (currentElement) {
      if (currentElement.hasAttribute('appScopedDragDrop') ||
          currentElement.classList.contains('scoped-drag-drop-container')) {
        this.shouldDisableDragAndDrop = true;
        return;
      }
      currentElement = currentElement.parentElement;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());

    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
      this.initTimeout = null;
    }
  }

  onSelectFile(files: FileList) {
    this.loading = true;
    this.changeDetectorRef.markForCheck();

    if (this.maxFiles && files.length > this.maxFiles) {
      const message = this.maxFiles === 1
        ? this.translateService.instant(T.common.you_can_only_upload_1_file_at_time)
        : this.translateService.instant(T.common.only_x_files_are_allowed, { count: this.maxFiles });
      void this.alertService.error(message);
      this.loading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    let validationResult: FileValidationResult;

    if (this.allowedTypes) {
      const validationOptions = {
        allowedTypes: this.allowedTypes,
        showErrors: true,
        showSummaryOnly: true
      };
      validationResult = this.fileValidationService.validateFilesWithOptions(files, validationOptions);
    } else {
      const validationOptions = {
        showErrors: true,
        showSummaryOnly: true
      };
      validationResult = this.fileValidationService.validateFilesWithOptions(files, validationOptions);
    }

    let finalValidFiles = validationResult.validFiles;
    if (this.maxFiles && finalValidFiles.length > this.maxFiles) {
      finalValidFiles = finalValidFiles.slice(0, this.maxFiles);
      const skippedCount = validationResult.validFiles.length - this.maxFiles;
      const message = this.maxFiles === 1
        ? this.translateService.instant(T.common.you_can_only_upload_1_file_at_time)
        : this.translateService.instant(T.common.only_first_x_files_were_selected_y_files_were_skipped, {
            maxFiles: this.maxFiles,
            skippedCount: skippedCount
          });
      void this.alertService.error(message);
    }

    this.loading = false;
    this.changeDetectorRef.markForCheck();
    this.onFilesAdded.next(finalValidFiles);
  }

  addLink() {
    this.onLinkAdded.next('');
  }

  onDropHandler(ev: DragEvent) {
    if (this.shouldDisableDragAndDrop) {
      return;
    }

    this.dragCounter = 0;
    this.isDragOver = false;
    this.onSelectFile(ev.dataTransfer ? ev.dataTransfer.files : null);
  }

  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    if (this.shouldDisableDragAndDrop) {
      return;
    }

    event.preventDefault();
    this.dragCounter++;
    if (this.dragCounter === 1) {
      this.isDragOver = true;
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (this.shouldDisableDragAndDrop) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    if (this.shouldDisableDragAndDrop) {
      return;
    }

    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
    if (this.dragCounter === 0) {
      this.isDragOver = false;
    }
  }

  openFileUpload() {
    if (this.fileElement) {
      const newEvent = new MouseEvent('click');
      this.fileElement.nativeElement.dispatchEvent(newEvent);
    }
  }

  async takePhoto() {
    try {
      this.loading = true;
      this.changeDetectorRef.markForCheck();

      const image = await Camera.getPhoto({
        quality: 70,
        allowEditing: false,
        source: CameraSource.Camera,
        resultType: CameraResultType.Base64,
        saveToGallery: false,
        correctOrientation: true,
        presentationStyle: 'popover'
      });

      if (!image || !image.base64String || image.base64String.trim() === '') {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
        return;
      }
      const blobData = CapacitorUtilities.b64toBlob(image.base64String, `image/${image.format}`);
      const imageName = `${uuidv4()}.png`;
      const imageFile = new File([blobData], imageName, { type: 'image/png' });

      const validationResult = this.fileValidationService.validateFiles([imageFile] as any, this.configuration);

      validationResult.validFiles.forEach(file => this.onFilesAdded.next([file]));

      // Don't set loading to false here - let parent component control it via externalLoading
      // The loading will be managed by the parent component's upload process
    } catch (error) {
      this.loading = false;
      this.changeDetectorRef.markForCheck();
      void this.alertService.error(this.translateService.instant(T.common.something_happened));
    }
  }

  getMaxFileSizeDisplay(): string {
    return this.fileValidationService.getMaxFileSizeDisplay(this.configuration);
  }
}
